import { Center, ThemeIcon } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { IconTrendingUp } from "@tabler/icons-react"
import { useNavigate } from "react-router-dom"

import { MetricsResponses } from "@costory/types/endpoints/metrics"
import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { MetricCard } from "@costory/front/pages/DigestV2/components/SuggestedCards/MetricCard"
import { PageType } from "@costory/front/pages/DigestV2/components/types"
import { useBusinessMetricsQuery } from "@costory/front/queries/businessMetrics"
import { useExplorerQuery } from "@costory/front/queries/explore"

const _SuggestedMetricsCard = ({
  metrics,
  form,
  initialValues,
  setChartType,
  setPage,
  setEditContent,
  editContent,
  isNotOtherNode,
  setLoading,
  filters,
}: {
  metrics: MetricsResponses.Metric[]
  form: UseFormReturnType<Filters>
  initialValues: Filters
  setChartType: (type: ExplorerChartType) => void
  setPage: (page: PageType) => void
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  isNotOtherNode: boolean
  setLoading?: (loading: boolean) => void
  filters: Filters
}) => {
  const navigate = useNavigate()
  const customMetric = metrics?.find((m) => m.technical === false)
  const handleNavigate = () => navigate("/formulas/new/default")
  const fallback = !customMetric ? (
    <Center
      style={{
        cursor: "pointer",
        color: "gray",
        fontSize: "14px",
        height: "100%",
      }}
      onClick={handleNavigate}
    >
      Set up custom cost formulas
    </Center>
  ) : undefined

  const onAnalyze = customMetric
    ? () => {
        setMetricForm(
          form,
          customMetric.id,
          initialValues,
          setChartType,
          setPage,
        )
      }
    : undefined

  // Always call the hook, but conditionally use its result
  const chartQuery = useExplorerQuery({
    ...initialValues,
    ...filters,
    metricId: customMetric?.id || "cost",
    aggBy: "Month",
    groupBy: "",
  })

  // Only use the query result if we have a custom metric
  const finalChartQuery = customMetric ? chartQuery : undefined

  return (
    <MetricCard
      icon={
        <ThemeIcon color="primaryHighlight">
          <IconTrendingUp size={20} />
        </ThemeIcon>
      }
      title="Marginal Cost"
      fallback={fallback}
      onAnalyze={onAnalyze}
      chartQuery={finalChartQuery}
      metricId={customMetric?.id}
      initialValues={{ ...initialValues, groupBy: "" }}
      form={form}
      setChartType={setChartType}
      setPage={setPage}
      setEditContent={setEditContent}
      editContent={editContent}
      isNotOtherNode={isNotOtherNode}
      setLoading={setLoading}
    />
  )
}

export const SuggestedMetricsCard = ({
  form,
  initialValues,
  setChartType,
  setPage,
  setEditContent,
  editContent,
  isNotOtherNode,
  setLoading,
  filters,
}: {
  form: UseFormReturnType<Filters>
  initialValues: Filters
  setChartType: (type: ExplorerChartType) => void
  setPage: (page: PageType) => void
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  isNotOtherNode: boolean
  setLoading?: (loading: boolean) => void
  filters: Filters
}) => {
  const metricsQuery = useBusinessMetricsQuery()
  return (
    <QueryWrapper query={metricsQuery}>
      {({ data: metrics }) => (
        <_SuggestedMetricsCard
          metrics={metrics}
          form={form}
          initialValues={initialValues}
          setChartType={setChartType}
          setPage={setPage}
          setEditContent={setEditContent}
          editContent={editContent}
          isNotOtherNode={isNotOtherNode}
          setLoading={setLoading}
          filters={filters}
        />
      )}
    </QueryWrapper>
  )
}

const setMetricForm = (
  form: UseFormReturnType<Filters>,
  metricId: string,
  initialValues: Filters,
  setChartType: (type: ExplorerChartType) => void,
  setPage: (page: PageType) => void,
) => {
  form.setValues(initialValues)
  form.setFieldValue("metricId", metricId)
  setChartType("LINE")
  setPage(PageType.AdvancedExplorer)
}
